import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { useAuth } from '../../shared/useAuth.js';
import { formatIQD } from '../../shared/format.js';
import { useAllowedWarehouses } from '../../shared/warehouseAccess.js';
import { useWarehouses } from '../products/hooks/useCategories.js';
import { useSuppliers } from './hooks/useSuppliers.js';

import { PurchasesList } from './components/PurchasesList.jsx';
import {
  usePurchases,
  useDeletePurchase,
  usePostPurchase,
  useCancelPurchase
} from './hooks/usePurchases.js';
import { ConfirmModal } from '../pricing/components/ConfirmModal.jsx';
import { PurchaseInvoiceForm } from './PurchaseInvoiceForm.jsx';

function ListView() {
  const navigate = useNavigate();
  const hasPermission = useAuth((s) => s.hasPermission);
  const canCreate = hasPermission('inventory.purchase.create');
  const canPost = hasPermission('inventory.purchase.post');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  // «عرض الكل» حالة مستقلة عن الفلاتر: تغيير أي فلتر لا يعيد الترقيم
  // بصمت — الفلاتر تُستعلم على الخادم فتشمل كل الفواتير لا الصفحة الظاهرة.
  const [showAll, setShowAll] = useState(false);

  // كل فلتر يُرجع الصفحة للأولى ولا يمسّ وضع «عرض الكل».
  function applyFilter(fn) {
    fn();
    setPage(1);
  }

  const { data: rawWarehouses = [] } = useWarehouses();
  const warehouses = useAllowedWarehouses(rawWarehouses);
  const { data: suppliersData } = useSuppliers();
  const suppliers = suppliersData?.items || [];

  const query = usePurchases({
    q: search || undefined,
    status: status || undefined,
    paymentStatus: paymentStatus || undefined,
    supplierId: supplierId || undefined,
    warehouseId: warehouseId || undefined,
    from: from || undefined,
    to: to || undefined,
    all: showAll || undefined,
    page,
    limit: 20
  });

  const deleteMut = useDeletePurchase();
  const postMut = usePostPurchase();
  const cancelMut = useCancelPurchase();

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmPost, setConfirmPost] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">المشتريات</h2>
          <div className="text-ink-3 text-xs mt-0.5">فواتير الشراء وإدارة المخزون الوارد</div>
        </div>
        {canCreate ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/purchase/new')}
          >
            <Icon name="plus" size={14} /> فاتورة شراء جديدة
          </button>
        ) : null}
      </div>

      <Card>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Icon name="search" size={14} className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none" />
            <input
              className="input pr-9 text-[12.5px]"
              placeholder="بحث برقم الفاتورة أو وصل المورد..."
              value={search}
              onChange={(e) => applyFilter(() => setSearch(e.target.value))}
            />
          </div>
          <select
            className="input text-[12.5px]"
            style={{ width: 130 }}
            value={status}
            onChange={(e) => applyFilter(() => setStatus(e.target.value))}
          >
            <option value="">كل الحالات</option>
            <option value="draft">مسودّة</option>
            <option value="posted">مرحّلة</option>
            <option value="cancelled">ملغاة</option>
          </select>
          <select
            className="input text-[12.5px]"
            style={{ width: 150 }}
            value={paymentStatus}
            onChange={(e) => applyFilter(() => setPaymentStatus(e.target.value))}
          >
            <option value="">كل حالات السداد</option>
            <option value="unpaid">غير مسددة</option>
            <option value="partial">مسددة جزئياً</option>
            <option value="paid">مسددة بالكامل</option>
          </select>
          <select
            className="input text-[12.5px]"
            style={{ width: 180 }}
            value={supplierId}
            onChange={(e) => applyFilter(() => setSupplierId(e.target.value))}
          >
            <option value="">كل الموردين</option>
            {suppliers.map((s) => (
              <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>
            ))}
          </select>
          <select
            className="input text-[12.5px]"
            style={{ width: 160 }}
            value={warehouseId}
            onChange={(e) => applyFilter(() => setWarehouseId(e.target.value))}
          >
            <option value="">كل المخازن</option>
            {warehouses.map((w) => (
              <option key={w._id || w.id} value={w._id || w.id}>{w.name}</option>
            ))}
          </select>
          <input
            className="input text-[12.5px]"
            type="date"
            style={{ width: 140 }}
            value={from}
            onChange={(e) => applyFilter(() => setFrom(e.target.value))}
          />
          <input
            className="input text-[12.5px]"
            type="date"
            style={{ width: 140 }}
            value={to}
            onChange={(e) => applyFilter(() => setTo(e.target.value))}
          />
          <button
            type="button"
            className={clsx('btn', showAll && 'btn-primary')}
            onClick={() => { setShowAll((v) => !v); setPage(1); }}
            title="عرض كل الفواتير في قائمة واحدة بلا ترقيم — الفلاتر تبقى مطبّقة"
          >
            <Icon name="inventory" size={13} /> {showAll ? 'عرض الكل (مفعّل)' : 'عرض الكل'}
          </button>
          {(search || status || paymentStatus || supplierId || warehouseId || from || to) ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => applyFilter(() => {
                setSearch(''); setStatus(''); setPaymentStatus('');
                setSupplierId(''); setWarehouseId(''); setFrom(''); setTo('');
              })}
            >
              <Icon name="x" size={13} /> مسح الفلاتر
            </button>
          ) : null}
        </div>
      </Card>

      <PurchasesList
        invoices={query.data?.items || []}
        loading={query.isLoading}
        page={query.data?.page || page}
        pages={query.data?.pages || 1}
        total={query.data?.total || 0}
        mode={query.data?.mode || 'page'}
        notice={query.data?.notice || ''}
        onPageChange={(p) => setPage(Math.max(1, p))}
        canEdit={canCreate}
        canPost={canPost}
        onPost={(inv) => setConfirmPost(inv)}
        onCancel={(inv) => setConfirmCancel(inv)}
        onDelete={(inv) => setConfirmDelete(inv)}
      />

      <ConfirmModal
        open={!!confirmPost}
        title="ترحيل فاتورة"
        message={`سيتم زيادة المخزون للأصناف وتسجيل ذمة المورد إن وُجدت. هل تريد المتابعة؟ (${confirmPost?.number || ''})`}
        confirmLabel="ترحيل"
        loading={postMut.isPending}
        onClose={() => setConfirmPost(null)}
        onConfirm={async () => {
          if (!confirmPost) return;
          await postMut.mutateAsync(
            { id: confirmPost._id || confirmPost.id, body: { updateCost: false, updateSalePrice: false } },
            { onSuccess: () => setConfirmPost(null) }
          );
        }}
      />

      {confirmCancel ? (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(15,16,18,0.5)' }}>
          <div className="bg-white rounded-[14px] shadow-card w-full overflow-hidden" style={{ maxWidth: 520 }}>
            <div className="card-h">
              <h3 className="text-[15px] font-bold flex-1 m-0">إلغاء فاتورة مرحّلة</h3>
              <button type="button" className="text-ink-3 hover:text-ink" onClick={() => setConfirmCancel(null)}>
                <Icon name="x" size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[13px] text-ink-2">
                الفاتورة <b className="font-mono">{confirmCancel.number}</b> لن تُحذف — ستُعلَّم «ملغاة»
                ويبقى كل سجلّها ظاهراً في كشف حساب المورد وسجلّ التدقيق.
              </div>
              <div className="card p-3 text-[12.5px] space-y-1" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
                <div>• تُعكس حركات المخزون لكل الأصناف.</div>
                <div>• يسقط التزام الفاتورة كاملاً من ذمة المورد.</div>
                <div>
                  • ما دُفع للمورد فعلاً ({formatIQD(confirmCancel.paid || 0)}) <b>لا يُعاد للقاصة تلقائياً</b>:
                  يُسجَّل رصيداً دائناً له عندنا، تحصّله لاحقاً بمستند «استرداد من المورد»
                  من شاشة حسابات الموردين، أو تخصمه من فاتورة قادمة.
                </div>
                <div>• لا يمكن إلغاء الفاتورة مرتين.</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">سبب الإلغاء</label>
                <input
                  className="input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="اختياري — يُحفظ في سجلّ التدقيق وكشف الحساب"
                />
              </div>
            </div>
            <div className="card-h border-b-0 border-t flex gap-2">
              <button type="button" className="btn flex-1 justify-center" onClick={() => setConfirmCancel(null)}>
                تراجع
              </button>
              <button
                type="button"
                className="btn btn-danger flex-1 justify-center"
                disabled={cancelMut.isPending}
                onClick={async () => {
                  await cancelMut.mutateAsync(
                    { id: confirmCancel._id || confirmCancel.id, reason: cancelReason.trim() },
                    { onSuccess: () => { setConfirmCancel(null); setCancelReason(''); } }
                  );
                }}
              >
                {cancelMut.isPending ? 'جاري الإلغاء...' : 'إلغاء الفاتورة'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف فاتورة"
        message={`هل أنت متأكد من حذف الفاتورة ${confirmDelete?.number || ''}؟ يُسمح فقط بحذف المسوّدات.`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteMut.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteMut.mutateAsync(
            confirmDelete._id || confirmDelete.id,
            { onSuccess: () => setConfirmDelete(null) }
          );
        }}
      />
    </div>
  );
}

function NormalizeWildcard() {
  // The sidebar NavLink renders the route's "path" prop literally; since our
  // path uses a wildcard ('/purchase/*'), clicking the sidebar link can land us
  // on '/purchase/*'. Redirect to the clean list URL.
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/purchase', { replace: true });
  }, [navigate]);
  return null;
}

export function PurchasesScreen() {
  const location = useLocation();
  if (location.pathname === '/purchase/*') {
    return <Navigate to="/purchase" replace />;
  }
  return (
    <Routes>
      <Route index element={<ListView />} />
      <Route path="new" element={<PurchaseInvoiceForm />} />
      <Route path=":id" element={<PurchaseInvoiceForm />} />
      <Route path="*" element={<NormalizeWildcard />} />
    </Routes>
  );
}
